/**
 * THI-263 — the centre's brand colour on the parent-facing surfaces.
 *
 * One nullable `#rrggbb` on the organisation. Null means the LopHoc default
 * (the green the pages shipped with), so a centre that never touches the
 * setting sees nothing change. The colour goes on the "shell" only: the sổ
 * liên lạc band, the selected-day ring, buttons, the manifest's theme
 * colour, the invoice's top band and labels. Attendance and payment colours
 * are semantic and never take it.
 */

export const BRAND_HEX_RE = /^#[0-9a-f]{6}$/i;

/** Eight presets offered in Cài đặt › Thông tin tổ chức. The first is the default green. */
export const BRAND_PRESETS: ReadonlyArray<{ hex: string; name: string }> = [
  { hex: '#1e6a4a', name: 'Xanh lá' },
  { hex: '#6b2140', name: 'Mận' },
  { hex: '#0f6e73', name: 'Xanh cổ vịt' },
  { hex: '#1f4e9c', name: 'Xanh dương' },
  { hex: '#7a3e1d', name: 'Nâu' },
  { hex: '#5b3a8c', name: 'Tím' },
  { hex: '#b5462b', name: 'Đỏ gạch' },
  { hex: '#2b2b2b', name: 'Than' },
];

export const DEFAULT_BRAND_COLOR = '#1e6a4a';

export function normalizeBrandColor(hex: string): string | null {
  const v = hex.trim().toLowerCase();
  return BRAND_HEX_RE.test(v) ? v : null;
}

function channel(v: number): number {
  const c = v / 255;
  return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
}

/** WCAG relative luminance of a `#rrggbb`. */
export function relativeLuminance(hex: string): number {
  const v = hex.replace('#', '');
  const r = parseInt(v.slice(0, 2), 16);
  const g = parseInt(v.slice(2, 4), 16);
  const b = parseInt(v.slice(4, 6), 16);
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}

/**
 * The text colour to put ON the brand colour: white unless white would fall
 * under WCAG AA (4.5:1), then a near-black. Never blocks a colour — a centre
 * that wants pale yellow gets dark text on it.
 */
export function brandOnColor(hex: string): '#ffffff' | '#000000' {
  const l = relativeLuminance(hex);
  const contrastWithWhite = 1.05 / (l + 0.05);
  // Black, not a near-black: with white cut off at 4.5:1 (luminance ≤ 0.183)
  // only a luminance ≤ 0.004 dark closes the gap so EVERY hex reaches AA.
  return contrastWithWhite >= 4.5 ? '#ffffff' : '#000000';
}

/** A shade of the brand colour (mixed toward black), for text on its tint. */
export function brandShade(hex: string, amount = 0.3): string {
  const v = hex.replace('#', '');
  const mix = (i: number) => Math.round(parseInt(v.slice(i, i + 2), 16) * (1 - amount)).toString(16).padStart(2, '0');
  return `#${mix(0)}${mix(2)}${mix(4)}`;
}

/**
 * A soft tint of the brand colour for the install card background and its
 * border, computed as a mix toward white so it works for any hue.
 */
export function brandTint(hex: string, amount = 0.9): string {
  const v = hex.replace('#', '');
  const mix = (i: number) => Math.round(parseInt(v.slice(i, i + 2), 16) * (1 - amount) + 255 * amount).toString(16).padStart(2, '0');
  return `#${mix(0)}${mix(2)}${mix(4)}`;
}

/**
 * The installed app's short name (THI-263 §2.2): the nickname the family
 * uses, else the last TWO words of the full name ("Nguyễn Minh Anh" → "Minh
 * Anh"), which is how a Vietnamese given name usually reads; a one-word name
 * is itself.
 */
export function progressAppShortName(name: string, nickname?: string | null): string {
  const nick = nickname?.trim();
  if (nick) return nick;
  const words = name.trim().split(/\s+/).filter(Boolean);
  return words.slice(-2).join(' ') || name.trim();
}
